import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Award, Shield, CheckCircle2, FileCheck } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { certifications } from '../mock';

const Certifications = () => {
  return (
    <div className="min-h-screen bg-white pt-20">
      <Helmet>
        <title>Certifications – FSSAI, IEC, APEDA | Agro Corp India</title>
        <meta name="description" content="Agro Corp India holds FSSAI, IEC, and APEDA certifications ensuring international food safety compliance for all exported agricultural products." />
        <meta name="keywords" content="FSSAI certified exporter, IEC import export code, APEDA registered, food safety certification India, agricultural export certification" />
        <link rel="canonical" href="https://www.agrocorpindia.com/certifications" />
      </Helmet>

      {/* Hero Section */}
      <section
        className="relative h-[50vh] flex items-center justify-center bg-cover bg-center"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.6)), url('https://images.unsplash.com/photo-1708266657611-4845bf82e48e')`,
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center z-10">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
            Certifications & Compliance
          </h1>
          <p className="text-xl text-green-100 max-w-3xl mx-auto">
            Meeting international standards for quality and safety
          </p>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-20 bg-gradient-to-b from-green-50 to-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-block mb-6">
            <Shield className="w-16 h-16 text-green-700 mx-auto" />
          </div>
          <h2 className="text-4xl font-bold text-green-900 mb-6">
            Quality You Can Trust
          </h2>
          <p className="text-lg text-gray-700 leading-relaxed">
            At AgroCorp India, we adhere to the highest standards of quality, safety, and compliance. 
            Our certifications reflect our commitment to delivering products that meet or exceed 
            international regulatory requirements.
          </p>
        </div>
      </section>

      {/* Certifications */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {certifications.map((cert) => (
              <Card
                key={cert.id}
                className="hover:shadow-2xl transition-all duration-300 border-2 hover:border-green-200 group"
              >
                <CardContent className="p-8 text-center">
                  <div className="w-24 h-24 bg-gradient-to-br from-green-100 to-green-200 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300">
                    <span className="text-5xl">{cert.logo}</span>
                  </div>
                  <h3 className="text-2xl font-bold text-green-900 mb-2">{cert.name}</h3>
                  <p className="text-amber-600 font-semibold mb-4 text-sm">{cert.fullName}</p>
                  <p className="text-gray-600">{cert.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Detailed Information */}
      <section className="py-20 bg-gradient-to-b from-green-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-green-900 mb-12 text-center">
            What Our Certifications Mean
          </h2>
          
          <div className="space-y-8">
            <Card className="border-l-4 border-green-600">
              <CardContent className="p-8">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <FileCheck className="w-6 h-6 text-green-700" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-green-900 mb-3">FSSAI Certification</h3>
                    <p className="text-gray-700 leading-relaxed mb-4">
                      The Food Safety and Standards Authority of India (FSSAI) certification ensures that all our 
                      products meet strict food safety and hygiene standards. This certification guarantees that our 
                      processing facilities, handling practices, and quality control measures comply with national 
                      food safety regulations.
                    </p>
                    <div className="space-y-2">
                      <div className="flex items-start space-x-2">
                        <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">Regular audits and inspections</span>
                      </div>
                      <div className="flex items-start space-x-2">
                        <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">Hygienic processing standards</span>
                      </div>
                      <div className="flex items-start space-x-2">
                        <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">Traceability and documentation</span>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-l-4 border-amber-600">
              <CardContent className="p-8">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Award className="w-6 h-6 text-amber-700" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-green-900 mb-3">IEC (Import Export Code)</h3>
                    <p className="text-gray-700 leading-relaxed mb-4">
                      Our IEC registration with the Directorate General of Foreign Trade (DGFT) authorizes us to 
                      engage in international trade. This certification is essential for customs clearance and 
                      demonstrates our compliance with Indian import-export regulations.
                    </p>
                    <div className="space-y-2">
                      <div className="flex items-start space-x-2">
                        <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">Legal authorization for exports</span>
                      </div>
                      <div className="flex items-start space-x-2">
                        <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">Customs compliance</span>
                      </div>
                      <div className="flex items-start space-x-2">
                        <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">International trade credibility</span>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-l-4 border-green-600">
              <CardContent className="p-8">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Shield className="w-6 h-6 text-green-700" />
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold text-green-900 mb-3">APEDA Registration</h3>
                    <p className="text-gray-700 leading-relaxed mb-4">
                      Registration with the Agricultural and Processed Food Products Export Development Authority 
                      (APEDA) ensures that our agricultural exports meet international quality standards. APEDA 
                      promotes exports of agricultural products and monitors quality compliance.
                    </p>
                    <div className="space-y-2">
                      <div className="flex items-start space-x-2">
                        <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">International quality standards</span>
                      </div>
                      <div className="flex items-start space-x-2">
                        <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">Export promotion support</span>
                      </div>
                      <div className="flex items-start space-x-2">
                        <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700">Quality monitoring and compliance</span>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Quality Commitment */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="bg-gradient-to-r from-green-700 to-green-800 text-white border-0">
            <CardContent className="p-12 text-center">
              <Award className="w-16 h-16 mx-auto mb-6 text-amber-400" />
              <h2 className="text-3xl font-bold mb-4">
                Our Commitment to Excellence
              </h2>
              <p className="text-lg text-green-100 leading-relaxed">
                These certifications are not just documents - they represent our unwavering commitment to 
                quality, safety, and customer satisfaction. We continuously invest in training, infrastructure, 
                and processes to maintain and exceed these standards.
              </p>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default Certifications;
